/**
 * EventDialog — the day's events as an expanding list.
 * @file src/web/features/calendar/components/EventDialog.tsx
 *
 * @llm-rule WHEN: A calendar day with events is opened
 * @llm-rule NOTE: Accordion, single-open by design — expanding one collapses
 *   whatever was open. The list scrolls, so a busy day stays navigable without
 *   the dialog growing past the viewport.
 */

import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@bloomneo/uikit';
import { Clock, ExternalLink, Lock, Trash2, Users, Video } from 'lucide-react';
import { PersonAvatar } from '../../../shared/components/presence';
import { ROLE_LABEL, type OrgRole } from '../../../shared/domain';

export interface CalendarEvent {
  id: string;
  title: string;
  notes: string | null;
  meetingLink: string | null;
  day: string;
  startTime: string;
  endTime: string | null;
  mine: boolean;
  /** Whether the viewer may delete it — mirrors the server's DELETE rule. */
  canRemove: boolean;
  /** EVENT is company-wide; REMINDER is private to its owner. */
  kind: 'EVENT' | 'REMINDER';
  owner: {
    id: string;
    name: string | null;
    orgRole: OrgRole;
    title: string | null;
    initials: string | null;
  };
}

/** Turn bare URLs in free text into links, leaving everything else alone. */
function Linkified({ text }: { text: string }) {
  const parts = text.split(/(https?:\/\/[^\s]+)/g);
  return (
    <>
      {parts.map((part, i) =>
        /^https?:\/\//.test(part) ? (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="break-all text-brand underline underline-offset-2"
          >
            {part}
          </a>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

export function EventDialog({
  open,
  onOpenChange,
  dayLabel,
  events,
  onDelete,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dayLabel: string;
  events: CalendarEvent[];
  onDelete: (id: string) => void;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Opening a day with a single event should show it straight away — making
  // someone click twice for one item is pure friction.
  useEffect(() => {
    if (!open) return;
    setExpandedId(events.length === 1 ? events[0].id : null);
  }, [open, events]);

  // Bring the newly expanded card into view when the list is long.
  useEffect(() => {
    if (!expandedId) return;
    const el = scrollRef.current?.querySelector(`[data-event="${expandedId}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [expandedId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{dayLabel}</DialogTitle>
          <DialogDescription>
            {events.length} event{events.length === 1 ? '' : 's'}
            {events.length > 1 && ' — tap one to open it'}
          </DialogDescription>
        </DialogHeader>

        <div
          ref={scrollRef}
          className="thin-scroll -mx-1 flex max-h-[52vh] flex-col gap-2 overflow-y-auto px-1 py-0.5"
        >
          {events.map((event) => {
            const expanded = expandedId === event.id;

            return (
              <div
                key={event.id}
                data-event={event.id}
                className={`overflow-hidden rounded-xl border transition-colors ${
                  expanded ? 'border-brand bg-paper' : 'border-rule bg-paper-warm'
                }`}
              >
                {/* Collapsed row — always visible, always the toggle. */}
                <button
                  type="button"
                  onClick={() => setExpandedId(expanded ? null : event.id)}
                  aria-expanded={expanded}
                  className="flex w-full items-center gap-3 px-3.5 py-3 text-left"
                >
                  <span className="flex w-[52px] flex-none flex-col items-start">
                    <span className="text-[12.5px] font-semibold text-ink">{event.startTime}</span>
                    {event.endTime && (
                      <span className="text-[10.5px] text-ink-ghost">{event.endTime}</span>
                    )}
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium text-ink">
                      {event.title}
                    </span>
                    <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-ink-faint">
                      {event.kind === 'REMINDER' && (
                        <span className="flex items-center gap-1 text-ink-ghost">
                          <Lock className="size-3" />
                          Private
                          <span className="text-ink-ghost">·</span>
                        </span>
                      )}
                      {event.mine ? (
                        'You'
                      ) : (
                        <>
                          <Users className="size-3 text-brand" />
                          {event.owner.name}
                          <span className="text-ink-ghost">
                            · {event.owner.title || ROLE_LABEL[event.owner.orgRole]}
                          </span>
                        </>
                      )}
                      {event.meetingLink && <Video className="size-3 text-brand" />}
                    </span>
                  </span>

                  <Clock
                    className={`size-3.5 flex-none transition-transform ${
                      expanded ? 'text-brand' : 'text-ink-ghost'
                    }`}
                  />
                </button>

                {/* Expanded detail */}
                {expanded && (
                  <div className="border-t border-rule px-3.5 py-3">
                    <div className="mb-2.5 flex items-center gap-2">
                      <PersonAvatar person={event.owner} size={26} />
                      <div className="min-w-0">
                        <div className="text-[12.5px] font-medium text-ink">
                          {event.mine ? 'You' : event.owner.name}
                        </div>
                        <div className="text-[11px] text-ink-faint">
                          {event.startTime}
                          {event.endTime ? ` – ${event.endTime}` : ''}
                        </div>
                      </div>
                    </div>

                    {event.meetingLink && (
                      <a
                        href={event.meetingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mb-2.5 flex items-center gap-2 rounded-lg bg-brand px-3 py-2 text-[12.5px] font-medium text-brand-ink hover:bg-brand/90"
                      >
                        <Video className="size-3.5" />
                        Join the call
                        <ExternalLink className="ml-auto size-3.5 opacity-70" />
                      </a>
                    )}

                    {event.notes ? (
                      <p className="whitespace-pre-wrap break-words text-[12.5px] leading-relaxed text-ink-soft">
                        <Linkified text={event.notes} />
                      </p>
                    ) : (
                      <p className="text-[12px] italic text-ink-ghost">No notes.</p>
                    )}

                    {/* Only what the server would actually let you remove. */}
                    {event.canRemove && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-3 h-7 rounded-full px-3 text-[11.5px] text-flag"
                        onClick={() => onDelete(event.id)}
                      >
                        <Trash2 className="mr-1.5 size-3" />
                        Remove
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
